// FEATURE 2: Shareable public startup report page
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';

// FIX: PRIORITY 5 - use the actual backend URL, not relative /api/
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const C = {
  bg: '#050510', surface: '#0d0d1f', border: 'rgba(255,255,255,0.06)',
  primary: '#7c6ff7', accent: '#00d4ff', green: '#00e676', amber: '#ffb800', danger: '#ff4466',
  text: '#f0f0ff', text2: '#8888aa', text3: '#444466',
};

interface ReportData {
  name: string; description?: string; stage?: string;
  targetSegment?: string; problemStatement?: string; uniqueValueProposition?: string;
  founderSummary?: string;
  nameValidation?: { riskScore: number; riskLevel: string; name: string } | null;
  keywords?: { totalOpportunity: { estimatedMonthlyTraffic: number }; quickWins: { term: string; volumeEstimate: string }[] } | null;
  competitors?: { competitors: { name: string; category: string }[] } | null;
  sentiment?: { overallSentiment: string; sentimentScore: number } | null;
}

export default function PublicReportPage() {
  const { startupId } = useParams<{ startupId: string }>();
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!startupId) return;
    fetch(`${API_BASE}/startups/${startupId}/public-report`)
      .then(r => r.json())
      .then(res => {
        if (res.success) setData(res.data);
        else setError('This report is not available.');
      })
      .catch(() => setError('Could not load report.'))
      .finally(() => setLoading(false));
  }, [startupId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: `conic-gradient(${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`, animation: 'spin 1.2s linear infinite', position: 'relative' }}>
          <div style={{ width: 34, height: 34, background: C.bg, borderRadius: '50%', position: 'absolute', top: 7, left: 7 }} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.text2, padding: 24 }}>
        <LogoLockup size="lg" />
        <p style={{ marginTop: 24, fontSize: 16 }}>{error || 'Report not found.'}</p>
        <a href="/" style={{ color: C.primary, marginTop: 16, fontSize: 14 }}>← Back to StartupScore</a>
      </div>
    );
  }

  const sentimentColor = data.sentiment?.overallSentiment === 'positive' ? C.green : data.sentiment?.overallSentiment === 'negative' ? C.danger : C.amber;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '48px 24px', color: C.text }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <LogoLockup size="md" />
          <div style={{ marginTop: 24, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.primary }}>
            Startup Report
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 900, marginTop: 8 }}>{data.name}</h1>
          {data.stage && <span style={{ display: 'inline-block', marginTop: 8, background: `${C.primary}20`, color: C.primary, borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{data.stage}</span>}
        </div>

        {data.description && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.6 }}>{data.description}</p>
          </div>
        )}

        {(data.targetSegment || data.problemStatement || data.uniqueValueProposition) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
            {data.targetSegment && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 10, color: C.text3, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Target</div>
                <div style={{ fontSize: 14, color: C.text }}>{data.targetSegment}</div>
              </div>
            )}
            {data.problemStatement && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 10, color: C.text3, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Problem</div>
                <div style={{ fontSize: 14, color: C.text }}>{data.problemStatement}</div>
              </div>
            )}
            {data.uniqueValueProposition && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 10, color: C.text3, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>UVP</div>
                <div style={{ fontSize: 14, color: C.text }}>{data.uniqueValueProposition}</div>
              </div>
            )}
          </div>
        )}

        {/* Intelligence Highlights */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {data.nameValidation && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: data.nameValidation.riskLevel === 'low' ? C.green : data.nameValidation.riskLevel === 'high' ? C.danger : C.amber }}>{data.nameValidation.riskScore}/10</div>
              <div style={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 600 }}>Name Risk</div>
            </div>
          )}
          {data.keywords && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: C.accent }}>{(data.keywords.totalOpportunity.estimatedMonthlyTraffic / 1000).toFixed(0)}K</div>
              <div style={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 600 }}>Traffic Opportunity</div>
            </div>
          )}
          {data.sentiment && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: sentimentColor }}>{data.sentiment.sentimentScore}%</div>
              <div style={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 600 }}>Sentiment</div>
            </div>
          )}
          {data.competitors && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: C.primary }}>{data.competitors.competitors.length}</div>
              <div style={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 600 }}>Competitors</div>
            </div>
          )}
        </div>

        {data.founderSummary && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Founder's Summary</h2>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: C.text2, lineHeight: 1.6, fontFamily: 'inherit' }}>{data.founderSummary}</pre>
          </div>
        )}

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 24, textAlign: 'center' }}>
          <p style={{ color: C.text2, fontSize: 14, marginBottom: 16 }}>
            Want to validate your own startup idea?
          </p>
          <a href="/auth?mode=signup" style={{
            display: 'inline-block', background: C.primary, color: '#fff', borderRadius: 12,
            padding: '12px 28px', fontSize: 15, fontWeight: 700, textDecoration: 'none',
          }}>
            Get Started Free
          </a>
        </div>
      </div>
    </div>
  );
}
